import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, } from 'recharts';
import { GetAuditoriaRateLimitStats } from './auditoria-rate-limit.service';
import type { AuditoriaRateLimitStatsDto } from './auditoria-rate-limit.types';

const CHART_HEIGHT = 200;
const CHART_CARD_MIN_HEIGHT = 220;

function ChartCard({
  titulo,
  descricao,
  children,
  vazio,
  mensagemVazio = 'Sem dados no período',
}: {
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
  vazio?: boolean;
  mensagemVazio?: string;
}) {
  return (
    <div className="card h-100">
      <div className="card-header">
        <h6 className="mb-0">{titulo}</h6>
        {descricao ? <small className="text-muted">{descricao}</small> : null}
      </div>
      <div
        className="card-body d-flex align-items-center justify-content-center"
        style={{ minHeight: CHART_CARD_MIN_HEIGHT }}
      >
        {vazio ? <span className="text-muted">{mensagemVazio}</span> : children}
      </div>
    </div>
  );
}

function mapSerieHora(
  items: { hora: string; quantidade: number }[] | undefined,
) {
  return (items ?? []).map((item) => ({
    ...item,
    horaLabel: format(new Date(item.hora), 'dd/MM HH:mm', { locale: ptBR }),
  }));
}

export default function RateLimitAuditoriaPage() {
  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

  const [dataInicial, setDataInicial] = useState(
    format(primeiroDia, 'yyyy-MM-dd'),
  );
  const [dataFinal, setDataFinal] = useState(format(ultimoDia, 'yyyy-MM-dd'));
  const [buscando, setBuscando] = useState(false);
  const [stats, setStats] = useState<AuditoriaRateLimitStatsDto | null>(null);

  const bloqueiosPorHora = useMemo(
    () => mapSerieHora(stats?.bloqueiosPorHora),
    [stats],
  );

  const permitidasPorHora = useMemo(
    () => mapSerieHora(stats?.permitidasPorHora),
    [stats],
  );

  async function handleBuscar() {
    setBuscando(true);
    try {
      const di = new Date(`${dataInicial}T00:00:00`);
      const df = new Date(`${dataFinal}T23:59:59`);
      const result = await GetAuditoriaRateLimitStats({
        data_inicial: di.toISOString(),
        data_final: df.toISOString(),
      });
      setStats(result);
    } catch (e) {
      toast.error(String(e));
    } finally {
      setBuscando(false);
    }
  }

  useEffect(() => {
    void handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const semDados =
    !stats ||
    (!stats.bloqueiosPorHora.length &&
      !stats.permitidasPorHora.length &&
      !stats.topIps.length &&
      !stats.porCategoria.length);

  return (
    <div className="row">
      <div className="col-12">
        <h3>Rate Limit</h3>
        <span className="blockquote-footer pb-4 text-end">
          Contadores em memória durante o dia; ao virar o dia, despejo em{' '}
          <code>logs/security.json</code> na API.
        </span>
      </div>
      <hr />

      <div className="col-12 mb-3 d-flex flex-wrap gap-2 align-items-end">
        <div className="d-flex align-items-center gap-2">
          <label htmlFor="rl-di" className="form-label mb-0 small text-nowrap">
            De
          </label>
          <input
            id="rl-di"
            type="date"
            className="form-control form-control-sm"
            value={dataInicial}
            onChange={(e) => setDataInicial(e.target.value)}
          />
        </div>
        <div className="d-flex align-items-center gap-2">
          <label htmlFor="rl-df" className="form-label mb-0 small text-nowrap">
            Até
          </label>
          <input
            id="rl-df"
            type="date"
            className="form-control form-control-sm"
            value={dataFinal}
            onChange={(e) => setDataFinal(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => void handleBuscar()}
          disabled={buscando}
        >
          {buscando ? 'Carregando…' : 'Atualizar'}
        </button>
      </div>

      {semDados ? (
        <div className="col-12">
          <div className="alert alert-info">
            Nenhuma métrica no período. O dia corrente aparece em tempo real (memória); dias
            anteriores vêm de <code>logs/security.json</code> após o despejo à meia-noite.
          </div>
        </div>
      ) : null}

      <div className="col-lg-6 mb-4">
        <ChartCard
          titulo="1. Bloqueios por hora (429)"
          descricao="Quando o limite foi excedido"
          vazio={!bloqueiosPorHora.length}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={bloqueiosPorHora}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="horaLabel" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="quantidade"
                name="Bloqueios"
                stroke="#dc3545"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="col-lg-6 mb-4">
        <ChartCard
          titulo="2. Auth permitidas por hora"
          descricao="Login, recuperação e token CPF aceitos"
          vazio={!permitidasPorHora.length}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={permitidasPorHora}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="horaLabel" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="quantidade"
                name="Permitidas"
                stroke="#198754"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="col-lg-6 mb-4">
        <ChartCard
          titulo="3. Top IPs bloqueados"
          descricao="Agregado por hora + IP"
          vazio={!stats?.topIps.length}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={stats?.topIps ?? []} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="rotulo" width={120} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="quantidade" name="Bloqueios" fill="#0d6efd" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="col-lg-6 mb-4">
        <ChartCard
          titulo="4. Por categoria (permitidas vs bloqueadas)"
          descricao="Totais no período selecionado"
          vazio={!stats?.porCategoria.length}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={stats?.porCategoria ?? []} margin={{ bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="permitidas" name="Permitidas" fill="#198754" radius={[4, 4, 0, 0]} />
              <Bar dataKey="bloqueadas" name="Bloqueadas" fill="#dc3545" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
